import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import ChevronRightIcon from '@mui/icons-material/esm/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import { RootState } from '../../store';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import { PageHeader } from '../../components/layout';
import roleWorkspaceService, { AccountsTransaction, AccountsWorkspace, RoleTaskSourceLink } from '../../services/api/roleWorkspaceService';
import accountsTransactionService from '../../services/api/accountsTransactionService';
import drawdownService, { DrawdownPacketRow, DrawdownPacketWithItems } from '../../services/api/drawdownService';
import procurementService, { PurchaseOrder, PurchaseOrderItemDTO, PurchaseOrderStatus } from '../../services/api/procurementService';
import { ACCOUNTS_PERSONNEL_QUERY_KEY, accountsFieldPersonnel, accountsFieldPersonnelById } from '../../routes/fieldPersonnel';

type AccountsFieldView = 'home' | 'construction-tasks' | 'purchase-orders' | 'other-transactions' | 'transactions' | 'drawdown';

interface AccountsPageProps {
  fieldMode?: boolean;
  view?: AccountsFieldView;
}

type TaskPayWorker = {
  id?: string | null;
  userId?: string | null;
  name?: string | null;
  completedAt?: string | null;
  selectionType?: string | null;
};

const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const dateOnlyParts = (value?: string | Date | null) => {
  if (typeof value !== 'string') return null;
  const match = value.match(dateOnlyPattern);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const transactionDateSortValue = (value?: string | Date | null) => {
  const parts = dateOnlyParts(value);
  if (parts) return Number(`${parts.year}${String(parts.month).padStart(2, '0')}${String(parts.day).padStart(2, '0')}`);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const fmtMoney = (value?: number | string | null) =>
  new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(Number(value || 0));

const fmtDate = (value?: string | Date | null) => {
  if (!value) return '-';
  const parts = dateOnlyParts(value);
  if (parts) return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString();
};

const taskTransactionFor = (taskId: string, transactions: AccountsTransaction[]) =>
  transactions.find((row) => (
    row.taskId === taskId
    || row.metadata?.sourceTaskId === taskId
    || row.metadata?.sourceRoleTaskId === taskId
  ) && ['recorded', 'reconciled'].includes(row.status)) ??
  transactions.find((row) => (
    row.taskId === taskId
    || row.metadata?.sourceTaskId === taskId
    || row.metadata?.sourceRoleTaskId === taskId
  ));

const workerPaymentKey = (worker?: { id?: string | null; userId?: string | null; name?: string | null } | null) =>
  worker?.id || worker?.userId || worker?.name || '';

const crewLabelForWorker = (
  crewName: string,
  worker?: { selectionType?: string | null } | null,
) => {
  const label = crewName || 'No crew assigned';
  return worker?.selectionType === 'alternative'
    ? `Alternative crew member (${label})`
    : label;
};

const taskTransactionForWorker = (
  taskId: string,
  worker: TaskPayWorker | null,
  transactions: AccountsTransaction[],
) => {
  const key = workerPaymentKey(worker);
  if (!key) return taskTransactionFor(taskId, transactions);
  const matchesTask = (row: AccountsTransaction) => (
    row.taskId === taskId
    || row.metadata?.sourceTaskId === taskId
    || row.metadata?.sourceRoleTaskId === taskId
  );
  const matchesWorker = (row: AccountsTransaction) =>
    row.metadata?.workerId === worker?.id
    || row.metadata?.workerUserId === worker?.userId
    || row.metadata?.workerName === worker?.name;
  return transactions.find((row) => matchesTask(row) && matchesWorker(row) && ['recorded', 'reconciled'].includes(row.status))
    ?? transactions.find((row) => matchesTask(row) && matchesWorker(row));
};

const isTaskPayTransaction = (tx: AccountsTransaction) =>
  metadataText(tx, 'transactionKind') === 'Task Pay'
  || Boolean((tx.taskId || tx.task) && !tx.purchaseOrderId && !tx.goodsReceiptId);

const isPoPayTransaction = (tx: AccountsTransaction) =>
  metadataText(tx, 'transactionKind') === 'PO Pay'
  || Boolean(tx.purchaseOrderId || tx.goodsReceiptId || tx.purchaseOrder || tx.goodsReceipt);

const sortTransactions = (rows: AccountsTransaction[]) =>
  [...rows].sort((a, b) => {
    const dateDiff = transactionDateSortValue(b.transactionDate) - transactionDateSortValue(a.transactionDate);
    if (dateDiff !== 0) return dateDiff;
    return a.description.localeCompare(b.description);
  });

const isPaidTransaction = (tx: AccountsTransaction) => ['recorded', 'reconciled'].includes(tx.status);

const monthKeyFor = (value?: string | Date | null) => {
  const parts = dateOnlyParts(value);
  if (parts) return `${parts.year}-${String(parts.month).padStart(2, '0')}`;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'undated';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabelFor = (key: string) => {
  if (key === 'undated') return 'No date';
  const date = new Date(`${key}-01T00:00:00`);
  return Number.isNaN(date.getTime())
    ? key
    : date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const groupTransactionsByMonth = (rows: AccountsTransaction[]) =>
  sortTransactions(rows).reduce<Array<{ key: string; label: string; rows: AccountsTransaction[]; total: number }>>((groups, tx) => {
    const key = monthKeyFor(tx.transactionDate);
    let group = groups.find((entry) => entry.key === key);
    if (!group) {
      group = { key, label: monthLabelFor(key), rows: [], total: 0 };
      groups.push(group);
    }
    group.rows.push(tx);
    if (isPaidTransaction(tx)) group.total += Number(tx.amount || 0);
    return groups;
  }, []);

const metadataText = (tx: AccountsTransaction, ...keys: string[]) => {
  for (const key of keys) {
    const value = tx.metadata?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const OTHER_TRANSACTION_CATEGORIES = [
  { value: 'security', label: 'Security', patterns: ['security'] },
  { value: 'janitor', label: 'Janitor', patterns: ['janitor', 'janitorial', 'cleaning'] },
  { value: 'utilities', label: 'Utilities', patterns: ['utilities', 'utility', 'water', 'electric', 'electricity', 'power'] },
  { value: 'office_expenses', label: 'Office Expenses', patterns: ['office expenses', 'office expense', 'office supplies', 'stationery'] },
];

const OTHER_TRANSACTION_PRESETS = [
  ...Array.from({ length: 5 }, (_, index) => ({
    value: `security_${index + 1}`,
    label: `Security ${index + 1}`,
    category: 'security',
    description: `Security ${index + 1} payment`,
    paymentFrequency: 'recurring',
  })),
  ...Array.from({ length: 3 }, (_, index) => ({
    value: `janitor_${index + 1}`,
    label: `Janitor ${index + 1}`,
    category: 'janitor',
    description: `Janitor ${index + 1} payment`,
    paymentFrequency: 'recurring',
  })),
  { value: 'office_supplies', label: 'Office supplies', category: 'office_expenses', description: 'Office supplies payment', paymentFrequency: 'one_off' },
  { value: 'stationery_printing', label: 'Stationery / printing', category: 'office_expenses', description: 'Stationery and printing payment', paymentFrequency: 'one_off' },
  { value: 'office_admin', label: 'Office administration', category: 'office_expenses', description: 'Office administration expense payment', paymentFrequency: 'one_off' },
];

const CUSTOM_OTHER_PAYMENT_PRESET = '__custom__';

const PAYMENT_FREQUENCIES = [
  { value: 'one_off', label: 'One-off payment' },
  { value: 'recurring', label: 'Recurring payment' },
];

const normalizedSearchText = (value: string) => value.toLowerCase().replace(/[_-]+/g, ' ');

const otherTransactionCategory = (tx: AccountsTransaction) => {
  const searchText = normalizedSearchText([
    metadataText(tx, 'category', 'expenseCategory', 'expenseType', 'title', 'name'),
    tx.description,
  ].filter(Boolean).join(' '));
  return OTHER_TRANSACTION_CATEGORIES.find((category) =>
    category.patterns.some((pattern) => searchText.includes(pattern))
  )?.label ?? '';
};

const leadNameFromTask = (tx: AccountsTransaction) => {
  const assignment = tx.task?.teamAssignments?.find((row) => row.completedAt) ?? tx.task?.teamAssignments?.[0];
  const lead = assignment?.team?.teamLead;
  return lead ? `${lead.firstName} ${lead.lastName}`.trim() : '';
};

const taskNameFromDescription = (description: string) => {
  const match = description.match(/(?:Worker|.+?) payment for\s+.+?\s+-\s+([^|]+)/i);
  return match?.[1]?.trim() ?? '';
};

const roleTaskCrewLeadName = (row: RoleTaskSourceLink) => {
  const assignment = row.sourceTask?.teamAssignments?.find((row) => row.completedAt) ?? row.sourceTask?.teamAssignments?.[0];
  const lead = assignment?.team?.teamLead;
  return lead ? `${lead.firstName} ${lead.lastName}`.trim() : '';
};

const paymentTaskIdFor = (row: RoleTaskSourceLink) =>
  row.sourceTask?.id ?? (row.sourceType === 'task' ? row.sourceId : row.task.id);

const accountsErrorMessage = (err: any, fallback: string) => {
  const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
  if (serverMessage) return serverMessage;
  if (err?.message === 'Network Error') {
    return `${fallback}. The app could not reach the server; check the connection and try again.`;
  }
  return err?.message || fallback;
};

const taskApplicationDate = (row: RoleTaskSourceLink) =>
  row.task.createdAt ?? (row.metadata?.applicationDate as string | undefined) ?? undefined;

const TX_STATUS_COLORS: Record<AccountsTransaction['status'], 'default' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  recorded: 'info',
  reconciled: 'success',
  rejected: 'error',
};

const PO_STATUS_COLORS: Partial<Record<PurchaseOrderStatus, 'default' | 'info' | 'warning' | 'success' | 'error'>> = {
  pending_approval: 'warning',
  approved: 'info',
  partial_received: 'warning',
  received: 'success',
  closed: 'success',
  cancelled: 'error',
};

const DRAWDOWN_STATUS_LABELS: Record<DrawdownPacketRow['status'], string> = {
  snapshot_created: 'Snapshot created',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
  voided: 'Voided',
};

const emptyTxForm = () => ({
  type: 'expense' as AccountsTransaction['type'],
  amount: 0,
  date: today(),
  description: '',
  preset: '',
  customPaymentItem: '',
  otherCategory: '',
  paymentFrequency: 'one_off',
  recurrenceDate: '',
});

export default function AccountsPage({ fieldMode = false, view = 'home' }: AccountsPageProps) {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<AccountsWorkspace | null>(null);
  const [transactions, setTransactions] = useState<AccountsTransaction[]>([]);
  const [drawdowns, setDrawdowns] = useState<DrawdownPacketRow[]>([]);
  const [drawdownDetails, setDrawdownDetails] = useState<Record<string, DrawdownPacketWithItems>>({});
  const [loadingDrawdownId, setLoadingDrawdownId] = useState<string | null>(null);
  const [drawdownsError, setDrawdownsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [actioningTxId, setActioningTxId] = useState<string | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<RoleTaskSourceLink | null>(null);
  const [selectedPaymentWorker, setSelectedPaymentWorker] = useState<TaskPayWorker | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', partPayment: '', date: today(), transactionNumber: '' });
  const [poPaymentOpen, setPoPaymentOpen] = useState(false);
  const [selectedPaymentPo, setSelectedPaymentPo] = useState<PurchaseOrder | null>(null);
  const [poPaymentForm, setPoPaymentForm] = useState({ amount: '', partPayment: '', date: today(), transactionNumber: '' });
  const [txOpen, setTxOpen] = useState(false);
  const [txForm, setTxForm] = useState(emptyTxForm());
  const [rejectTx, setRejectTx] = useState<AccountsTransaction | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTouched, setRejectTouched] = useState(false);
  const [expandedTransactionId, setExpandedTransactionId] = useState<string | null>(null);
  const [poPayDetails, setPoPayDetails] = useState<Record<string, PurchaseOrder>>({});
  const [loadingPoPayId, setLoadingPoPayId] = useState<string | null>(null);
  const [poPayError, setPoPayError] = useState<string | null>(null);
  const allowPersonnelSelection = ['admin', 'field_admin'].includes(user?.role ?? '');
  const selectedPersonnel = allowPersonnelSelection
    ? accountsFieldPersonnelById(searchParams.get(ACCOUNTS_PERSONNEL_QUERY_KEY))
    : null;
  const selectedTransactionGroup = searchParams.get('txGroup');

  useEffect(() => {
    // Slice 4 removed the per-browser localStorage "books"; clear any stale keys
    // so previously fabricated POs/payroll/history cannot resurface anywhere.
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('accounts-field:')) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
  }, []);

  const load = async () => {
    if (!activeProjectId) return;
    setError(null);
    try {
      const [workspace, txRows] = await Promise.all([
        roleWorkspaceService.accounts(activeProjectId),
        accountsTransactionService.list(activeProjectId),
      ]);
      setData(workspace);
      setTransactions(txRows);
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to load accounts workspace'));
    }
    try {
      const packets = await drawdownService.listPackets({ projectId: activeProjectId, limit: 100 });
      setDrawdowns(packets.packets ?? []);
      setDrawdownsError(null);
    } catch (err: any) {
      setDrawdowns([]);
      setDrawdownsError(err.response?.data?.message || err.response?.data?.error || 'Failed to load drawdown packets');
    }
  };

  const loadDrawdownDetail = async (packetId: string, expanded: boolean) => {
    if (!expanded || drawdownDetails[packetId] || loadingDrawdownId === packetId) return;
    try {
      setLoadingDrawdownId(packetId);
      const packet = await drawdownService.getPacket(packetId);
      if (packet) {
        setDrawdownDetails((current) => ({ ...current, [packetId]: packet }));
      }
    } catch (err: any) {
      setDrawdownsError(err.response?.data?.message || err.response?.data?.error || 'Failed to load drawdown packet details');
    } finally {
      setLoadingDrawdownId(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  const constructionTaskPayLinks = useMemo(
    () => (data?.tasks ?? []).filter((row) => row.triggerType === 'construction_approved_accounts'),
    [data?.tasks],
  );

  const allTaskRows = useMemo(() => constructionTaskPayLinks.map((row) => {
    const paymentTaskId = paymentTaskIdFor(row);
    const sourceTask = row.sourceTask ?? null;
    const assignment = sourceTask?.teamAssignments?.find((row) => row.completedAt) ?? sourceTask?.teamAssignments?.[0] ?? null;
    const team = assignment?.team ?? null;
    const lead = team?.teamLead ?? null;
    const completedWorkers = sourceTask?.completedDispatchWorkers?.length ? sourceTask.completedDispatchWorkers : [];
    const workers = completedWorkers.length
      ? completedWorkers
      : [{ name: lead ? `${lead.firstName} ${lead.lastName}` : 'Unassigned', completedAt: assignment?.completedAt ?? sourceTask?.actualEndDate ?? sourceTask?.completedAt ?? null }];
    return workers.map((worker) => {
      const tx = taskTransactionForWorker(paymentTaskId, worker, transactions) ?? taskTransactionForWorker(row.task.id, worker, transactions);
      const amount = tx ? Number(tx.amount) : Number(
        sourceTask?.paymentAmount
        ?? sourceTask?.taskValue
        ?? row.task.paymentAmount
        ?? row.task.taskValue
        ?? row.metadata?.taskValue
        ?? 0
      );
      const appliedDate = tx?.transactionDate
        ?? worker.completedAt
        ?? assignment?.completedAt
        ?? sourceTask?.actualEndDate
        ?? sourceTask?.completedAt
        ?? taskApplicationDate(row)
        ?? '';
      return {
        id: `${row.task.id}:${workerPaymentKey(worker) || 'crew'}`,
        number: row.task.taskCode,
        name: row.task.name,
        workerName: worker.name || 'Unassigned',
        worker,
        crewName: crewLabelForWorker(team?.name ?? 'No crew assigned', worker),
        sourceTaskCode: sourceTask?.taskCode ?? (row.metadata?.taskCode as string | undefined) ?? row.task.taskCode,
        sourceTaskName: sourceTask?.name ?? row.task.name,
        recordedDate: appliedDate,
        fullPay: Number.isFinite(amount) ? amount : null,
        status: tx ? (tx.status === 'reconciled' ? 'Paid' : tx.status === 'recorded' ? 'Recorded' : 'Draft') : 'Pending',
        tx,
        source: row,
      };
    });
  }).flat(), [constructionTaskPayLinks, transactions]);
  const taskRows = useMemo(
    () => allTaskRows.filter((row) => !['Recorded', 'Paid'].includes(row.status)),
    [allTaskRows],
  );

  const paidPoIds = useMemo(
    () => new Set(transactions.filter((tx) => isPoPayTransaction(tx) && isPaidTransaction(tx) && tx.purchaseOrderId).map((tx) => tx.purchaseOrderId as string)),
    [transactions],
  );
  const approvalPos = useMemo(
    () => (data?.approvalPurchaseOrders ?? []).filter((po) => !paidPoIds.has(po.id)),
    [data?.approvalPurchaseOrders, paidPoIds],
  );
  const closePos = useMemo(
    () => (data?.closePurchaseOrders ?? []).filter((po) => !paidPoIds.has(po.id)),
    [data?.closePurchaseOrders, paidPoIds],
  );
  const allActivePos = useMemo(
    () => (data?.purchaseOrders ?? []).filter((po) => !paidPoIds.has(po.id)),
    [data?.purchaseOrders, paidPoIds],
  );
  const poQueue = useMemo(() => {
    if (allActivePos.length > 0) return allActivePos;
    const byId = new Map<string, PurchaseOrder>();
    [...approvalPos, ...closePos].forEach((po) => byId.set(po.id, po));
    return [...byId.values()];
  }, [allActivePos, approvalPos, closePos]);
  const poQueueTotal = poQueue.reduce((sum, po) => sum + Number(po.totalAmount || 0), 0);
  const otherTransactions = useMemo(
    () => sortTransactions(transactions.filter((tx) => !isTaskPayTransaction(tx) && !isPoPayTransaction(tx))),
    [transactions],
  );
  const completedTransactions = useMemo(
    () => sortTransactions(transactions.filter(isPaidTransaction)),
    [transactions],
  );
  const transactionGroups = useMemo(() => {
    const completedOtherTransactions = completedTransactions.filter((tx) => !isTaskPayTransaction(tx) && !isPoPayTransaction(tx));
    const taskPay = completedTransactions.filter(isTaskPayTransaction);
    const poPay = completedTransactions.filter(isPoPayTransaction);
    return [
      {
        key: 'task-pay',
        title: 'Task Pay',
        subtitle: 'Worker and crew task payment transactions',
        rows: taskPay,
      },
      {
        key: 'po-pay',
        title: 'PO Pay',
        subtitle: 'Supplier purchase order and delivery payment transactions',
        rows: poPay,
      },
      {
        key: 'other',
        title: 'Other Payments',
        subtitle: 'Security, janitor, utilities, office expenses, adjustments, revenue, and unlinked entries',
        rows: completedOtherTransactions,
      },
    ];
  }, [completedTransactions]);

  const clearSelectedPersonnel = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(ACCOUNTS_PERSONNEL_QUERY_KEY);
    nextParams.set('view', 'accounts');
    setSearchParams(nextParams);
  };

  const openSelectedPersonnel = (personnelId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'accounts');
    nextParams.set(ACCOUNTS_PERSONNEL_QUERY_KEY, personnelId);
    setSearchParams(nextParams);
  };

  const personnelIndicatorGrid = (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>Accounts Personnel</Typography>
      <Grid container spacing={1.5}>
        {accountsFieldPersonnel.map((person) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
            <PersonnelFolderCard
              name={person.name}
              email={person.email}
              credentialHint={person.credentialHint}
              onClick={() => openSelectedPersonnel(person.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const personnelContextCard = selectedPersonnel ? (
    <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">
            Accounts personnel
          </Typography>
          <Typography variant="h5" fontWeight={900}>{selectedPersonnel.name}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip size="small" label={selectedPersonnel.roleLabel} />
            <Chip size="small" label={selectedPersonnel.team} />
            <Chip size="small" color="success" label={selectedPersonnel.telegramStatus} />
            <Chip size="small" label={`Login: ${selectedPersonnel.email}`} />
            {selectedPersonnel.credentialHint && <Chip size="small" label={selectedPersonnel.credentialHint} />}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${poQueue.length} PO`} />
          <Chip size="small" label={`${taskRows.length} task payments`} />
          <Chip size="small" label={`${transactions.length} transactions`} />
          <Chip size="small" label={`${drawdowns.length} drawdowns`} />
          <Button size="small" variant="outlined" onClick={clearSelectedPersonnel} sx={{ borderRadius: 1, fontWeight: 900 }}>
            Back to all Accounts
          </Button>
        </Stack>
      </Stack>
    </Paper>
  ) : null;

  const taskPaymentPayload = (
    row: RoleTaskSourceLink,
    worker: TaskPayWorker | null,
    amount: number,
    transactionDate: string,
    transactionNumber?: string,
    partPayment?: number,
  ) => {
    const sourceTask = row.sourceTask ?? null;
    const paymentTaskId = paymentTaskIdFor(row);
    const taskCode = sourceTask?.taskCode ?? row.task.taskCode;
    const taskName = sourceTask?.name ?? row.task.name;
    const workerName = worker?.name || roleTaskCrewLeadName(row);
    const completedAssignment = sourceTask?.teamAssignments?.find((row) => row.completedAt) ?? sourceTask?.teamAssignments?.[0];
    const baseCrewName = completedAssignment?.team?.name ?? '';
    const crewName = crewLabelForWorker(baseCrewName, worker);
    const descriptionParts = [`${workerName || 'Worker'} payment for ${taskCode} - ${taskName}`];
    if (transactionNumber !== undefined) descriptionParts.push(`Transaction #: ${transactionNumber || 'N/A'}`);
    if (partPayment !== undefined) descriptionParts.push(`Part payment: ${fmtMoney(Number(partPayment || 0))}`);
    return {
      projectId: activeProjectId!,
      type: 'expense' as const,
      amount,
      transactionDate,
      description: descriptionParts.join(' | '),
      taskId: paymentTaskId,
      metadata: {
        transactionKind: 'Task Pay',
        sourceTaskId: paymentTaskId,
        sourceRoleTaskId: row.task.id,
        sourceLinkId: row.id,
        workerId: worker?.id,
        workerUserId: worker?.userId,
        workerName,
        workerSelectionType: worker?.selectionType,
        crewName,
        taskCode,
        taskName,
      },
    };
  };

  const poTransactionFor = (poId: string) =>
    transactions.find((row) => row.purchaseOrderId === poId && ['recorded', 'reconciled'].includes(row.status))
    ?? transactions.find((row) => row.purchaseOrderId === poId);

  const poPaymentPayload = (
    po: PurchaseOrder,
    amount: number,
    transactionDate: string,
    transactionNumber?: string,
    partPayment?: number,
  ) => {
    const supplierName = po.supplier?.name || po.supplierId;
    const descriptionParts = [`Material payment for PO ${po.poNumber} - ${supplierName}`];
    if (partPayment && partPayment > 0 && partPayment !== amount) descriptionParts.push(`Part payment: ${fmtMoney(partPayment)}`);
    if (transactionNumber?.trim()) descriptionParts.push(`Transaction #: ${transactionNumber.trim()}`);
    return {
      projectId: po.projectId,
      type: 'expense' as const,
      amount,
      transactionDate,
      description: descriptionParts.join(' | '),
      purchaseOrderId: po.id,
      metadata: {
        transactionKind: 'PO Pay',
        poNumber: po.poNumber,
        supplierName,
        materialLineCount: po.items?.length ?? 0,
      },
    };
  };

  const completeTaskPayment = async (row: { id: string; source: RoleTaskSourceLink; worker: TaskPayWorker | null; tx?: AccountsTransaction | null; fullPay: number | null; workerName: string }) => {
    if (!activeProjectId) return;
    if (row.tx && ['recorded', 'reconciled'].includes(row.tx.status)) return;
    if (!window.confirm(`Complete payment for ${row.workerName}? Only this worker payment will be completed.`)) return;
    try {
      setCompletingId(row.id);
      setError(null);
      if (row.tx?.status === 'draft') {
        await accountsTransactionService.record(row.tx.id);
      } else {
        const amount = Number(row.fullPay ?? 0);
        if (!Number.isFinite(amount) || amount <= 0) {
          throw new Error('Payment amount is missing for this worker');
        }
        const created = await accountsTransactionService.create(taskPaymentPayload(row.source, row.worker, amount, today()));
        await accountsTransactionService.record(created.id);
      }
      await load();
      if (fieldMode) {
        navigate('/field/accounts/transactions?txGroup=task-pay');
      } else {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('txGroup', 'task-pay');
        setSearchParams(nextParams);
      }
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to complete worker payment'));
    } finally {
      setCompletingId(null);
    }
  };

  const openPayment = (row: RoleTaskSourceLink, worker?: TaskPayWorker | null) => {
    const paymentTaskId = paymentTaskIdFor(row);
    const tx = taskTransactionForWorker(paymentTaskId, worker ?? null, transactions) ?? taskTransactionForWorker(row.task.id, worker ?? null, transactions);
    setSelectedTask(row);
    setSelectedPaymentWorker(worker ?? null);
    setPaymentForm({
      amount: tx ? String(tx.amount) : '',
      partPayment: tx ? String(tx.amount) : '',
      date: tx?.transactionDate ?? today(),
      transactionNumber: tx?.description?.match(/Transaction #:\s*([^|]+)/)?.[1]?.trim() ?? '',
    });
    setPaymentOpen(true);
  };

  const saveTaskPayment = async () => {
    if (!activeProjectId || !selectedTask) return;
    setSaving(true);
    try {
      await accountsTransactionService.create(taskPaymentPayload(
        selectedTask,
        selectedPaymentWorker,
        Number(paymentForm.amount || 0),
        paymentForm.date,
        paymentForm.transactionNumber,
        Number(paymentForm.partPayment || 0),
      ));
      setPaymentOpen(false);
      await load();
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to save task payment'));
    } finally {
      setSaving(false);
    }
  };

  const openPoPayment = (po: PurchaseOrder) => {
    const tx = poTransactionFor(po.id);
    setSelectedPaymentPo(po);
    setPoPaymentForm({
      amount: tx ? String(tx.amount) : String(Number(po.totalAmount || 0) || ''),
      partPayment: tx ? String(tx.amount) : '',
      date: tx?.transactionDate ?? today(),
      transactionNumber: tx?.description?.match(/Transaction #:\s*([^|]+)/)?.[1]?.trim() ?? '',
    });
    setPoPaymentOpen(true);
  };

  const savePoPayment = async () => {
    if (!selectedPaymentPo) return;
    setSaving(true);
    try {
      await accountsTransactionService.create(poPaymentPayload(
        selectedPaymentPo,
        Number(poPaymentForm.amount || 0),
        poPaymentForm.date,
        poPaymentForm.transactionNumber,
        Number(poPaymentForm.partPayment || 0),
      ));
      setPoPaymentOpen(false);
      await load();
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to save PO payment'));
    } finally {
      setSaving(false);
    }
  };

  const completePoPayment = async (po: PurchaseOrder) => {
    const tx = poTransactionFor(po.id);
    if (tx && ['recorded', 'reconciled'].includes(tx.status)) return;
    if (!window.confirm(`Complete material payment for PO ${po.poNumber}?`)) return;
    try {
      setCompletingId(`po-${po.id}`);
      setError(null);
      if (tx?.status === 'draft') {
        await accountsTransactionService.record(tx.id);
      } else {
        const amount = Number(po.totalAmount || 0);
        if (!Number.isFinite(amount) || amount <= 0) throw new Error('PO payment amount is missing');
        const created = await accountsTransactionService.create(poPaymentPayload(po, amount, today()));
        await accountsTransactionService.record(created.id);
      }
      await load();
      if (fieldMode) {
        navigate('/field/accounts/transactions?txGroup=po-pay');
      } else {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('txGroup', 'po-pay');
        setSearchParams(nextParams);
      }
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to complete PO payment'));
    } finally {
      setCompletingId(null);
    }
  };

  const transactionLinkLabel = (tx: AccountsTransaction) =>
    tx.task?.taskCode ?? tx.purchaseOrder?.poNumber ?? tx.goodsReceipt?.grnNumber;

  const loadPurchaseOrderDetail = async (purchaseOrderId: string) => {
    if (poPayDetails[purchaseOrderId] || loadingPoPayId === purchaseOrderId) return;
    try {
      setLoadingPoPayId(purchaseOrderId);
      setPoPayError(null);
      const po = await procurementService.getPurchaseOrder(purchaseOrderId);
      setPoPayDetails((current) => ({ ...current, [purchaseOrderId]: po }));
    } catch (err: any) {
      setPoPayError(err.response?.data?.error || err.response?.data?.message || 'Failed to load purchase order products');
    } finally {
      setLoadingPoPayId(null);
    }
  };

  const loadPoPayDetail = async (tx: AccountsTransaction) => {
    if (!tx.purchaseOrderId) return;
    await loadPurchaseOrderDetail(tx.purchaseOrderId);
  };

  const openRejectDialog = (tx: AccountsTransaction) => {
    setRejectTx(tx);
    setRejectReason('');
    setRejectTouched(false);
    setError(null);
  };

  const closeRejectDialog = () => {
    if (actioningTxId) return;
    setRejectTx(null);
    setRejectReason('');
    setRejectTouched(false);
  };

  const submitRejectTransaction = async () => {
    if (!rejectTx) return;
    const reason = rejectReason.trim();
    setRejectTouched(true);
    if (!reason) {
      setError('A rejection reason is required.');
      return;
    }
    try {
      setActioningTxId(rejectTx.id);
      setError(null);
      await accountsTransactionService.reject(rejectTx.id, reason);
      setRejectTx(null);
      setRejectReason('');
      setRejectTouched(false);
      await load();
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to reject transaction'));
    } finally {
      setActioningTxId(null);
    }
  };

  const runTransactionAction = async (tx: AccountsTransaction, action: 'record' | 'reconcile') => {
    const linked = tx.task?.taskCode ?? tx.purchaseOrder?.poNumber ?? tx.goodsReceipt?.grnNumber;
    const prompt = action === 'record'
      ? `Record ${fmtMoney(tx.amount)} ${tx.type}${linked ? ` linked to ${linked}` : ''}? Recorded transactions can no longer be edited.`
      : `Reconcile ${fmtMoney(tx.amount)} ${tx.type}${linked ? ` linked to ${linked}` : ''}? Reconciled is the final state.`;
    if (!window.confirm(prompt)) return;
    try {
      setActioningTxId(tx.id);
      setError(null);
      if (action === 'record') await accountsTransactionService.record(tx.id);
      else await accountsTransactionService.reconcile(tx.id);
      await load();
    } catch (err: any) {
      setError(accountsErrorMessage(err, `Failed to ${action} transaction`));
    } finally {
      setActioningTxId(null);
    }
  };

  const applyOtherTransactionPreset = (presetValue: string) => {
    if (presetValue === CUSTOM_OTHER_PAYMENT_PRESET) {
      setTxForm((current) => ({
        ...current,
        preset: presetValue,
        customPaymentItem: '',
        description: '',
      }));
      return;
    }
    const preset = OTHER_TRANSACTION_PRESETS.find((row) => row.value === presetValue);
    setTxForm((current) => ({
      ...current,
      preset: presetValue,
      customPaymentItem: '',
      otherCategory: preset?.category ?? current.otherCategory,
      description: preset?.description ?? current.description,
      paymentFrequency: preset?.paymentFrequency ?? current.paymentFrequency,
    }));
  };

  const saveNewTransaction = async () => {
    if (!activeProjectId) return;
    setSaving(true);
    try {
      const categoryLabel = OTHER_TRANSACTION_CATEGORIES.find((category) => category.value === txForm.otherCategory)?.label;
      const paymentFrequencyLabel = PAYMENT_FREQUENCIES.find((frequency) => frequency.value === txForm.paymentFrequency)?.label;
      const customPaymentItem = txForm.customPaymentItem.trim();
      const presetLabel = txForm.preset === CUSTOM_OTHER_PAYMENT_PRESET
        ? customPaymentItem
        : OTHER_TRANSACTION_PRESETS.find((preset) => preset.value === txForm.preset)?.label;
      await accountsTransactionService.create({
        projectId: activeProjectId,
        type: txForm.type,
        amount: Number(txForm.amount),
        transactionDate: txForm.date,
        description: txForm.description.trim(),
        metadata: {
          transactionKind: 'Payment',
          ...(presetLabel ? { payee: presetLabel, presetCode: txForm.preset } : {}),
          ...(txForm.otherCategory ? { category: categoryLabel, categoryCode: txForm.otherCategory } : {}),
          paymentFrequency: paymentFrequencyLabel,
          paymentFrequencyCode: txForm.paymentFrequency,
          ...(txForm.paymentFrequency === 'recurring' ? { recurrenceDate: txForm.recurrenceDate } : {}),
        },
      });
      setTxOpen(false);
      setTxForm(emptyTxForm());
      await load();
    } catch (err: any) {
      setError(accountsErrorMessage(err, 'Failed to create transaction'));
    } finally {
      setSaving(false);
    }
  };

  const fieldMetricCard = (label: string, value: string, note?: string) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minHeight: 96 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.15, mt: 0.5 }}>{value}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
  );

  const fieldMenuBar = (label: string, detail: string, to: string, amount?: string) => (
    <Button
      component={RouterLink}
      to={to}
      fullWidth
      variant="outlined"
      sx={{
        justifyContent: 'space-between',
        minHeight: 70,
        px: 2,
        borderRadius: 1,
        textAlign: 'left',
        color: 'text.primary',
        borderColor: 'divider',
      }}
    >
      <Box>
        <Typography fontWeight={900}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      <Typography fontWeight={900}>{amount || 'Details >'}</Typography>
    </Button>
  );

  const poRow = (po: PurchaseOrder, to: string) => {
    const tx = poTransactionFor(po.id);
    const paid = tx && ['recorded', 'reconciled'].includes(tx.status);
    return (
      <Paper key={po.id} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={1}
        sx={{ px: 1.5, py: 1, minHeight: 56 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} noWrap>{po.poNumber}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{po.supplier?.name || po.supplierId}</Typography>
        </Box>
        <Typography fontWeight={800}>{fmtMoney(po.totalAmount)}</Typography>
        <Chip size="small" color={PO_STATUS_COLORS[po.status] ?? 'default'} label={po.status.replace(/_/g, ' ')} />
        <Chip size="small" color={paid ? 'success' : tx?.status === 'draft' ? 'warning' : 'default'} label={paid ? 'Payment recorded' : tx?.status === 'draft' ? 'Payment drafted' : 'Payment pending'} />
        <Stack direction="row" spacing={0.75} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="outlined" component={RouterLink} to={to} endIcon={<ChevronRightIcon />}>
            View details
          </Button>
          <Button size="small" variant="outlined" disabled={Boolean(paid)} onClick={() => openPoPayment(po)}>
            {paid ? 'Payment recorded' : tx?.status === 'draft' ? 'Update payment' : 'Record payment'}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={Boolean(paid) || completingId === `po-${po.id}`}
            onClick={() => completePoPayment(po)}
          >
            {completingId === `po-${po.id}` ? 'Completing...' : paid ? 'Completed' : 'Complete'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
    );
  };

  const taskStatusChip = (status: string) => (
    <Chip
      size="small"
      color={status === 'Paid' ? 'success' : status === 'Recorded' ? 'info' : 'warning'}
      label={status}
    />
  );

  const renderTaskQueueTable = () => (
    <Paper sx={{ p: 1.5, borderRadius: 1 }}>
      <Typography fontWeight={900} sx={{ mb: 1 }}>Certified Task Payments</Typography>
      {taskRows.length === 0 && <Alert severity="info">No construction task payments in the accounts queue.</Alert>}
      {taskRows.length > 0 && (
        <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 0.75, fontSize: 12 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Crew</TableCell>
              <TableCell>Task</TableCell>
              <TableCell>Applied</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {taskRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={800}>{row.workerName}</Typography>
                </TableCell>
                <TableCell>{row.crewName}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={800}>{row.sourceTaskCode}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.sourceTaskName}</Typography>
                </TableCell>
                <TableCell>{fmtDate(row.recordedDate)}</TableCell>
                <TableCell>{taskStatusChip(row.status)}</TableCell>
                <TableCell align="right">{row.fullPay !== null ? fmtMoney(row.fullPay) : '-'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => openPayment(row.source, row.worker)}>
                      {row.tx ? 'Update payment' : 'Record payment'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={completingId === row.id || row.status === 'Recorded' || row.status === 'Paid'}
                      onClick={() => completeTaskPayment(row)}
                    >
                      {completingId === row.id ? 'Completing...' : row.status === 'Recorded' || row.status === 'Paid' ? 'Completed' : 'Complete'}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );

  const taskPayPersonName = (tx: AccountsTransaction) => {
    const queuedRow = taskRows.find((row) => row.tx?.id === tx.id || paymentTaskIdFor(row.source) === tx.taskId || row.source.task.id === tx.taskId);
    return metadataText(tx, 'workerName', 'completedByName', 'completedBy')
      || queuedRow?.workerName
      || leadNameFromTask(tx)
      || 'Worker payment';
  };

  const taskPayTaskName = (tx: AccountsTransaction) =>
    metadataText(tx, 'taskName')
    || tx.task?.name
    || taskNameFromDescription(tx.description)
    || 'Task name not linked';

  const poPayTitle = (tx: AccountsTransaction) =>
    tx.purchaseOrder?.poNumber
    || metadataText(tx, 'poNumber', 'purchaseOrderNumber')
    || tx.goodsReceipt?.grnNumber
    || 'Purchase order payment';

  const poItemName = (item: PurchaseOrderItemDTO) =>
    item.resource?.name
    || item.description
    || item.resourceId
    || 'Purchased product';

  const poItemQuantity = (item: PurchaseOrderItemDTO) => {
    const quantity = Number(item.quantityReceived || item.quantityOrdered || 0);
    const unit = item.resource?.unitOfMeasure;
    return `${Number.isFinite(quantity) ? quantity.toLocaleString() : '0'}${unit ? ` ${unit}` : ''}`;
  };

  const purchaseOrderMaterialsSummary = (po: PurchaseOrder | null | undefined, paidAmount: number) => {
    const poItems = po?.items ?? [];
    return (
      <Stack spacing={1}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>{fieldMetricCard('Paid', fmtMoney(paidAmount), 'recorded / reconciled')}</Grid>
          <Grid item xs={12} sm={4}>{fieldMetricCard('Purchase order', po?.poNumber ?? '-', po?.supplier?.name ?? '')}</Grid>
          <Grid item xs={12} sm={4}>{fieldMetricCard('Materials', String(poItems.length), po ? fmtMoney(po.totalAmount) : 'PO total')}</Grid>
        </Grid>
        {loadingPoPayId === po?.id && <LinearProgress sx={{ borderRadius: 1 }} />}
        {poPayError && <Alert severity="warning">{poPayError}</Alert>}
        {po && poItems.length === 0 && <Alert severity="info">No material lines are listed on this purchase order.</Alert>}
        {poItems.length > 0 && (
          <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Material</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell align="right">Unit cost</TableCell>
                <TableCell align="right">Line amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {poItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={800}>{poItemName(item)}</Typography>
                    {item.description && item.resource?.name && (
                      <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{poItemQuantity(item)}</TableCell>
                  <TableCell align="right">{fmtMoney(item.unitCost)}</TableCell>
                  <TableCell align="right">{fmtMoney(Number(item.quantityOrdered || 0) * Number(item.unitCost || 0))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    );
  };

  const otherTransactionTitle = (tx: AccountsTransaction) =>
    metadataText(tx, 'title', 'name')
    || tx.description.split('|')[0]?.trim()
    || 'Transaction';

  const transactionFrequencyLabel = (tx: AccountsTransaction) =>
    metadataText(tx, 'paymentFrequency')
    || (metadataText(tx, 'paymentFrequencyCode') === 'recurring' ? 'Recurring payment' : '')
    || (metadataText(tx, 'paymentFrequencyCode') === 'one_off' ? 'One-off payment' : '');

  const transactionTypeLabel = (tx: AccountsTransaction, isOther: boolean) =>
    isOther ? metadataText(tx, 'transactionKind') || 'Payment' : tx.type;

  const transactionActions = (tx: AccountsTransaction) => (
    <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
      {tx.status === 'draft' && (
        <>
          <Button size="small" variant="outlined" disabled={actioningTxId === tx.id} onClick={() => runTransactionAction(tx, 'record')}>
            {actioningTxId === tx.id ? 'Recording...' : 'Record'}
          </Button>
          <Button size="small" variant="outlined" color="error" disabled={actioningTxId === tx.id} onClick={() => openRejectDialog(tx)}>
            Reject
          </Button>
        </>
      )}
      {tx.status === 'recorded' && (
        <>
          <Button size="small" variant="outlined" color="success" disabled={actioningTxId === tx.id} onClick={() => runTransactionAction(tx, 'reconcile')}>
            {actioningTxId === tx.id ? 'Reconciling...' : 'Reconcile'}
          </Button>
          <Button size="small" variant="outlined" color="error" disabled={actioningTxId === tx.id} onClick={() => openRejectDialog(tx)}>
            Reject
          </Button>
        </>
      )}
      {tx.status === 'reconciled' && <Typography variant="caption" color="text.secondary">Final</Typography>}
      {tx.status === 'rejected' && <Typography variant="caption" color="error">Rejected</Typography>}
    </Stack>
  );

  const transactionCard = (tx: AccountsTransaction, groupKey: string) => {
    const rejectionReason = typeof tx.metadata?.rejectionReason === 'string' ? tx.metadata.rejectionReason : '';
    const expanded = expandedTransactionId === tx.id;
    const isTaskPay = groupKey === 'task-pay';
    const isPoPay = groupKey === 'po-pay';
    const isReconciledPoPay = isPoPay && tx.status === 'reconciled';
    const otherCategory = !isTaskPay && !isPoPay ? otherTransactionCategory(tx) : '';
    const paymentFrequency = !isTaskPay && !isPoPay ? transactionFrequencyLabel(tx) : '';
    const recurrenceDate = !isTaskPay && !isPoPay ? metadataText(tx, 'recurrenceDate') : '';
    const typeLabel = transactionTypeLabel(tx, !isTaskPay && !isPoPay);
    const processingHref = isPoPay && !isReconciledPoPay && tx.purchaseOrderId
      ? `${fieldMode ? '/field/purchase-orders' : '/procurement'}/${tx.purchaseOrderId}`
      : '';
    const title = isTaskPay ? taskPayPersonName(tx) : isPoPay ? poPayTitle(tx) : otherTransactionTitle(tx);
    const poDetail = tx.purchaseOrderId ? poPayDetails[tx.purchaseOrderId] : null;
    const openTransactionCard = () => {
      setExpandedTransactionId(expanded ? null : tx.id);
      if (isReconciledPoPay && !expanded) void loadPoPayDetail(tx);
    };
    const detailLabel = isReconciledPoPay
      ? (expanded ? 'Hide materials' : 'View materials paid')
      : isPoPay
        ? (tx.status === 'recorded' ? 'Open to reconcile' : 'Open to record')
        : (expanded ? 'Hide details' : 'View details');
    const cardContent = (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ width: '100%' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={900} noWrap>{title}</Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{fmtDate(tx.transactionDate)}</Typography>
            <Chip size="small" variant="outlined" label={typeLabel} />
            {otherCategory && <Chip size="small" color="info" label={otherCategory} />}
            {paymentFrequency && <Chip size="small" variant="outlined" label={paymentFrequency} />}
            {recurrenceDate && <Chip size="small" variant="outlined" label={`Recurs ${fmtDate(recurrenceDate)}`} />}
            <Chip size="small" color={TX_STATUS_COLORS[tx.status]} label={tx.status} />
          </Stack>
        </Box>
        <Typography fontWeight={900}>{fmtMoney(tx.amount)}</Typography>
        <Chip
          size="small"
          variant="outlined"
          label={detailLabel}
        />
      </Stack>
    );

    return (
      <Paper key={tx.id} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {processingHref ? (
          <Button
            component={RouterLink}
            to={processingHref}
            fullWidth
            sx={{ p: 1.25, color: 'inherit', textAlign: 'left', textTransform: 'none', justifyContent: 'flex-start' }}
          >
            {cardContent}
          </Button>
        ) : (
          <Button
            fullWidth
            onClick={openTransactionCard}
            sx={{ p: 1.25, color: 'inherit', textAlign: 'left', textTransform: 'none', justifyContent: 'flex-start' }}
          >
            {cardContent}
          </Button>
        )}
        {expanded && isReconciledPoPay && (
          <Box sx={{ px: 1.25, pb: 1.25 }}>
            {!tx.purchaseOrderId && <Alert severity="info">This PO Pay transaction has no linked purchase order.</Alert>}
            {tx.purchaseOrderId && purchaseOrderMaterialsSummary(poDetail, Number(tx.amount || 0))}
          </Box>
        )}
        {expanded && !isPoPay && (
          <Box sx={{ px: 1.25, pb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">
              {isTaskPay ? 'Task completed' : 'Transaction'}
            </Typography>
            <Typography variant="body2" fontWeight={800}>
              {isTaskPay ? taskPayTaskName(tx) : otherTransactionTitle(tx)}
            </Typography>
            {rejectionReason && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                Rejected: {rejectionReason}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ px: 1.25, pb: 1.25 }}>
          {transactionActions(tx)}
        </Box>
      </Paper>
    );
  };

  const renderMonthlyTransactions = (rows: AccountsTransaction[], groupKey: string, emptyLabel: string) => {
    const months = groupTransactionsByMonth(rows);
    if (months.length === 0) return <Alert severity="info">No {emptyLabel} transactions yet.</Alert>;
    return (
      <Stack spacing={1}>
        {months.map((month) => (
          <Accordion
            key={month.key}
            disableGutters
            variant="outlined"
            sx={{ borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.75}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{ width: '100%', pr: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={900}>{month.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {month.rows.length} transaction{month.rows.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={900}>{fmtMoney(month.total)} paid</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={0.75}>{month.rows.map((tx) => transactionCard(tx, groupKey))}</Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    );
  };

  const renderOtherTransactionsPage = () => (
    <Paper sx={{ p: 1.5, borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography fontWeight={900}>Other Payments</Typography>
          <Typography variant="body2" color="text.secondary">Security, janitor, utilities, and office expense payments.</Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setTxForm(emptyTxForm()); setTxOpen(true); }}>
          Create transaction
        </Button>
      </Stack>
      {otherTransactions.length === 0 && <Alert severity="info">No other payments have been created yet.</Alert>}
      {otherTransactions.length > 0 && renderMonthlyTransactions(otherTransactions, 'other', 'other payment')}
    </Paper>
  );

  const renderTransactionsTable = () => (
    <Paper sx={{ p: 1.5, borderRadius: 1 }}>
      <Typography fontWeight={900} sx={{ mb: 1 }}>Completed Transactions</Typography>
      {completedTransactions.length === 0 && <Alert severity="info">No completed transactions yet for this project.</Alert>}
      {completedTransactions.length > 0 && (
        <Stack spacing={1}>
          {transactionGroups.map((group) => {
            const total = group.rows
              .filter(isPaidTransaction)
              .reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
            return (
              <Accordion
                key={group.key}
                disableGutters
                variant="outlined"
                defaultExpanded={selectedTransactionGroup === group.key || (!selectedTransactionGroup && group.key === 'task-pay' && group.rows.length > 0)}
                sx={{ borderRadius: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={0.75}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={900}>{group.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{group.subtitle}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip size="small" label={`${group.rows.length} transaction${group.rows.length === 1 ? '' : 's'}`} />
                      <Typography variant="body2" fontWeight={900}>{fmtMoney(total)} paid</Typography>
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {renderMonthlyTransactions(group.rows, group.key, group.title.toLowerCase())}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Paper>
  );

  const renderPurchaseOrderQueues = (linkBase: string) => (
    <Stack spacing={1.5}>
      <Paper sx={{ p: 1.5, borderRadius: 1 }}>
        <Typography fontWeight={900} sx={{ mb: 1 }}>All Active Purchase Orders ({poQueue.length})</Typography>
        <Stack spacing={1}>
          {poQueue.map((po) => poRow(po, `${linkBase}/${po.id}`))}
          {poQueue.length === 0 && <Alert severity="info">No active purchase orders are waiting in Accounts.</Alert>}
        </Stack>
      </Paper>
      <Paper sx={{ p: 1.5, borderRadius: 1 }}>
        <Typography fontWeight={900} sx={{ mb: 1 }}>Awaiting Approval ({approvalPos.length})</Typography>
        <Stack spacing={1}>
          {approvalPos.map((po) => poRow(po, `${linkBase}/${po.id}`))}
          {approvalPos.length === 0 && <Alert severity="info">No purchase orders awaiting approval.</Alert>}
        </Stack>
      </Paper>
      <Paper sx={{ p: 1.5, borderRadius: 1 }}>
        <Typography fontWeight={900}>Ready for Financial Close ({closePos.length})</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Closing a received PO requires the linked receipt transaction to be recorded or reconciled in Transactions.
        </Typography>
        <Stack spacing={1}>
          {closePos.map((po) => poRow(po, `${linkBase}/${po.id}`))}
          {closePos.length === 0 && <Alert severity="info">No purchase orders awaiting financial close.</Alert>}
        </Stack>
      </Paper>
    </Stack>
  );

  const drawdownPacketMenu = (rows: DrawdownPacketRow[]) => (
    <Stack spacing={1}>
      {rows.map((packet) => {
        const detail = drawdownDetails[packet.id];
        const items = detail?.items ?? [];
        return (
          <Accordion
            key={packet.id}
            disableGutters
            variant="outlined"
            onChange={(_event, expanded) => loadDrawdownDetail(packet.id, expanded)}
            sx={{ borderRadius: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.75}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{ width: '100%', pr: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={900} noWrap>{packet.drawdownReference}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Snapshot {fmtDate(packet.snapshotGeneratedAt)} - {packet.itemCount} item{packet.itemCount === 1 ? '' : 's'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={packet.status === 'paid' ? 'success' : packet.status === 'rejected' || packet.status === 'voided' ? 'error' : 'info'}
                    label={DRAWDOWN_STATUS_LABELS[packet.status]}
                  />
                  <Typography variant="body2" fontWeight={900}>{fmtMoney(packet.netAmount)}</Typography>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {loadingDrawdownId === packet.id && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
              <Grid container spacing={1} sx={{ mb: items.length > 0 ? 1 : 0 }}>
                <Grid item xs={6} sm={3}>{fieldMetricCard('Gross', fmtMoney(packet.grossAmount), 'certified')}</Grid>
                <Grid item xs={6} sm={3}>{fieldMetricCard('Retention', fmtMoney(packet.retentionAmount), 'held')}</Grid>
                <Grid item xs={6} sm={3}>{fieldMetricCard('Net', fmtMoney(packet.netAmount), 'payable')}</Grid>
                <Grid item xs={6} sm={3}>{fieldMetricCard('Submitted', fmtDate(packet.submittedAt), `paid ${fmtDate(packet.paidAt)}`)}</Grid>
              </Grid>
              {!detail && loadingDrawdownId !== packet.id && (
                <Typography variant="caption" color="text.secondary">Open to load packet line items.</Typography>
              )}
              {detail && items.length === 0 && (
                <Alert severity="info">No line items are linked to this drawdown packet.</Alert>
              )}
              {items.length > 0 && (
                <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.75, fontSize: 12 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Task</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell align="right">Retention</TableCell>
                      <TableCell align="right">Net</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800}>
                            {item.qsCertificateLine?.lineCode ?? item.drawdownReference ?? 'Drawdown item'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.qsCertificateLine?.description ?? item.description}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.task ? `${item.task.taskCode} - ${item.task.name ?? 'Task'}` : '-'}</TableCell>
                        <TableCell align="right">{fmtMoney(item.grossAmount)}</TableCell>
                        <TableCell align="right">{fmtMoney(item.retentionAmount)}</TableCell>
                        <TableCell align="right">{fmtMoney(item.netAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );

  const renderDrawdownView = () => {
    const paidTotal = drawdowns
      .filter((packet) => packet.status === 'paid')
      .reduce((sum, packet) => sum + Number(packet.netAmount || 0), 0);
    const awaitingTotal = drawdowns
      .filter((packet) => ['submitted', 'approved'].includes(packet.status))
      .reduce((sum, packet) => sum + Number(packet.netAmount || 0), 0);
    const [latest, ...previous] = drawdowns;
    return (
      <Stack spacing={1.5}>
        <Typography variant="h4" fontWeight={900}>Accountant</Typography>
        <Typography variant="h6" fontWeight={900}>QS Claim Drawdown</Typography>
        <Typography variant="caption" color="text.secondary">
          Drawdown packets are prepared and submitted from the QS workspace — shown here read-only.
        </Typography>
        {drawdownsError && <Alert severity="warning">{drawdownsError}</Alert>}
        {!drawdownsError && drawdowns.length === 0 && (
          <Alert severity="info">No drawdown packets yet for this project.</Alert>
        )}
        {latest && (
          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>Latest Drawdown Packet</Typography>
            {drawdownPacketMenu([latest])}
          </Paper>
        )}
        {previous.length > 0 && (
          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>Previous Drawdown Packets</Typography>
            {drawdownPacketMenu(previous)}
          </Paper>
        )}
        {drawdowns.length > 0 && (
          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Typography fontWeight={900}>Drawdown Totals</Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              <Grid item xs={4}>{fieldMetricCard('Paid to date', fmtMoney(paidTotal), 'net of retention')}</Grid>
              <Grid item xs={4}>{fieldMetricCard('Awaiting payment', fmtMoney(awaitingTotal), 'submitted / approved')}</Grid>
              <Grid item xs={4}>{fieldMetricCard('Packets', String(drawdowns.length), 'total')}</Grid>
            </Grid>
          </Paper>
        )}
      </Stack>
    );
  };

  const paymentDialog = (
    <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{selectedTask ? `Apply payment - ${selectedTask.task.taskCode}` : 'Apply payment'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField type="number" label="Part payment" value={paymentForm.partPayment} onChange={(event) => setPaymentForm({ ...paymentForm, partPayment: event.target.value })} />
          <TextField type="date" label="Date" InputLabelProps={{ shrink: true }} value={paymentForm.date} onChange={(event) => setPaymentForm({ ...paymentForm, date: event.target.value })} />
          <TextField type="number" label="Amount transferred" value={paymentForm.amount} onChange={(event) => setPaymentForm({ ...paymentForm, amount: event.target.value })} />
          <TextField label="Transaction #" value={paymentForm.transactionNumber} onChange={(event) => setPaymentForm({ ...paymentForm, transactionNumber: event.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>
        <Button variant="contained" disabled={saving || Number(paymentForm.amount || 0) <= 0} onClick={saveTaskPayment}>Save payment</Button>
      </DialogActions>
    </Dialog>
  );

  const poPaymentDialog = (
    <Dialog open={poPaymentOpen} onClose={() => setPoPaymentOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{selectedPaymentPo ? `Material payment - ${selectedPaymentPo.poNumber}` : 'Material payment'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {selectedPaymentPo && purchaseOrderMaterialsSummary(selectedPaymentPo, Number(poPaymentForm.amount || 0))}
          <TextField
            type="number"
            label="Part payment"
            value={poPaymentForm.partPayment}
            onChange={(event) => setPoPaymentForm({ ...poPaymentForm, partPayment: event.target.value })}
          />
          <TextField
            type="date"
            label="Date"
            InputLabelProps={{ shrink: true }}
            value={poPaymentForm.date}
            onChange={(event) => setPoPaymentForm({ ...poPaymentForm, date: event.target.value })}
          />
          <TextField
            type="number"
            label="Amount transferred"
            value={poPaymentForm.amount}
            onChange={(event) => setPoPaymentForm({ ...poPaymentForm, amount: event.target.value })}
          />
          <TextField
            label="Transaction #"
            value={poPaymentForm.transactionNumber}
            onChange={(event) => setPoPaymentForm({ ...poPaymentForm, transactionNumber: event.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPoPaymentOpen(false)}>Cancel</Button>
        <Button variant="contained" disabled={saving || Number(poPaymentForm.amount || 0) <= 0} onClick={savePoPayment}>Save payment</Button>
      </DialogActions>
    </Dialog>
  );

  const newTransactionDialog = (
    <Dialog open={txOpen} onClose={() => setTxOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>New other payment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Payment item</InputLabel>
            <Select
              value={txForm.preset}
              label="Payment item"
              onChange={(event) => applyOtherTransactionPreset(event.target.value)}
            >
              <MenuItem value="">Select payment item</MenuItem>
              {OTHER_TRANSACTION_PRESETS.map((preset) => (
                <MenuItem key={preset.value} value={preset.value}>{preset.label}</MenuItem>
              ))}
              <MenuItem value={CUSTOM_OTHER_PAYMENT_PRESET}>Create new payment item</MenuItem>
            </Select>
          </FormControl>
          {txForm.preset === CUSTOM_OTHER_PAYMENT_PRESET && (
            <TextField
              label="New payment item"
              value={txForm.customPaymentItem}
              onChange={(event) => {
                const customPaymentItem = event.target.value;
                setTxForm({
                  ...txForm,
                  customPaymentItem,
                  description: customPaymentItem.trim() ? `${customPaymentItem.trim()} payment` : '',
                });
              }}
            />
          )}
          <TextField label="Type" value="Payment" disabled />
          <TextField type="number" label="Amount" value={txForm.amount} onChange={(event) => setTxForm({ ...txForm, amount: Number(event.target.value) })} />
          <TextField type="date" label="Transaction date" InputLabelProps={{ shrink: true }} value={txForm.date} onChange={(event) => setTxForm({ ...txForm, date: event.target.value })} />
          <TextField multiline minRows={2} label="Description" value={txForm.description} onChange={(event) => setTxForm({ ...txForm, description: event.target.value })} />
          <FormControl fullWidth>
            <InputLabel>Other payment category</InputLabel>
            <Select
              value={txForm.otherCategory}
              label="Other payment category"
              onChange={(event) => setTxForm({ ...txForm, preset: '', customPaymentItem: '', otherCategory: event.target.value })}
            >
              <MenuItem value="">Select category</MenuItem>
              {OTHER_TRANSACTION_CATEGORIES.map((category) => (
                <MenuItem key={category.value} value={category.value}>{category.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Payment frequency</InputLabel>
            <Select
              value={txForm.paymentFrequency}
              label="Payment frequency"
              onChange={(event) => setTxForm({
                ...txForm,
                paymentFrequency: event.target.value,
                recurrenceDate: event.target.value === 'recurring' ? txForm.recurrenceDate : '',
              })}
            >
              {PAYMENT_FREQUENCIES.map((frequency) => (
                <MenuItem key={frequency.value} value={frequency.value}>{frequency.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {txForm.paymentFrequency === 'recurring' && (
            <TextField
              type="date"
              label="Recurring payment date"
              InputLabelProps={{ shrink: true }}
              value={txForm.recurrenceDate}
              onChange={(event) => setTxForm({ ...txForm, recurrenceDate: event.target.value })}
              helperText="Select the date this payment should recur."
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTxOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving || txForm.amount <= 0 || !txForm.description.trim() || !txForm.date || !txForm.otherCategory || (txForm.preset === CUSTOM_OTHER_PAYMENT_PRESET && !txForm.customPaymentItem.trim()) || (txForm.paymentFrequency === 'recurring' && !txForm.recurrenceDate)}
          onClick={saveNewTransaction}
        >
          Save other payment
        </Button>
      </DialogActions>
    </Dialog>
  );

  const rejectTransactionDialog = (
    <Dialog open={Boolean(rejectTx)} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Reject transaction</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {rejectTx && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
              <Typography fontWeight={900}>{rejectTx.description}</Typography>
              <Typography variant="body2" color="text.secondary">
                {fmtMoney(rejectTx.amount)} {rejectTx.type}
                {transactionLinkLabel(rejectTx) ? ` - ${transactionLinkLabel(rejectTx)}` : ''}
              </Typography>
            </Paper>
          )}
          <TextField
            label="Rejection reason"
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            onBlur={() => setRejectTouched(true)}
            multiline
            minRows={3}
            required
            autoFocus
            error={rejectTouched && !rejectReason.trim()}
            helperText={rejectTouched && !rejectReason.trim() ? 'Enter a reason before rejecting.' : 'This reason will be saved against the rejected transaction.'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeRejectDialog} disabled={Boolean(actioningTxId)}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={Boolean(actioningTxId) || !rejectReason.trim()}
          onClick={submitRejectTransaction}
        >
          Confirm rejection
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (fieldMode) {
    return (
      <Stack spacing={1.5}>
        {!activeProjectId && <Alert severity="warning">Select an active project to load accounts data.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {personnelContextCard}

        {view === 'home' && allowPersonnelSelection && !selectedPersonnel && personnelIndicatorGrid}

        {view === 'home' && (!allowPersonnelSelection || selectedPersonnel) && (
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>Accountant</Typography>
            <Grid container spacing={1}>
              <Grid item xs={3}>{fieldMetricCard('PO approvals', String(approvalPos.length), 'pending')}</Grid>
              <Grid item xs={3}>{fieldMetricCard('Task payments', String(taskRows.length), 'in queue')}</Grid>
              <Grid item xs={3}>{fieldMetricCard('PO close', String(closePos.length), 'awaiting close')}</Grid>
              <Grid item xs={3}>{fieldMetricCard('Drawdown', String(drawdowns.length), 'claims')}</Grid>
            </Grid>
            <Typography fontWeight={900}>Expense Payment Requests</Typography>
            {fieldMenuBar(
              'Purchase Order Payments',
              'Approvals and financial close',
              '/field/accounts/purchase-orders',
              poQueue.length ? fmtMoney(poQueueTotal) : undefined,
            )}
            {fieldMenuBar(
              'Construction Task Payments',
              'Certified task payment queue',
              '/field/accounts/construction-tasks',
              taskRows.length ? `${taskRows.length} due` : undefined,
            )}
            {fieldMenuBar(
              'Other Payments',
              'Security, janitor, utilities and office expenses',
              '/field/accounts/other-transactions',
              otherTransactions.length ? `${otherTransactions.length} total` : undefined,
            )}
            {fieldMenuBar(
              'Completed Transactions',
              'Recorded task, PO and other payments',
              '/field/accounts/transactions',
              completedTransactions.length ? `${completedTransactions.length} total` : undefined,
            )}
            <Typography fontWeight={900}>Income Request</Typography>
            {fieldMenuBar(
              'QS Drawdown Claims',
              'Drawdown packets (read-only)',
              '/field/accounts/drawdown',
              drawdowns.length ? fmtMoney(drawdowns[0].netAmount) : undefined,
            )}
          </Stack>
        )}

        {view === 'purchase-orders' && (
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>Accountant</Typography>
            <Typography variant="h6" fontWeight={900}>Purchase Order Payments</Typography>
            {renderPurchaseOrderQueues('/field/purchase-orders')}
          </Stack>
        )}

        {view === 'construction-tasks' && (
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>Accountant</Typography>
            <Typography variant="h6" fontWeight={900}>Construction Task Payments</Typography>
            {renderTaskQueueTable()}
          </Stack>
        )}

        {view === 'transactions' && (
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>Accountant</Typography>
            <Typography variant="h6" fontWeight={900}>Completed Transactions</Typography>
            {renderTransactionsTable()}
          </Stack>
        )}

        {view === 'other-transactions' && (
          <Stack spacing={1.5}>
            <Typography variant="h4" fontWeight={900}>Accountant</Typography>
            <Typography variant="h6" fontWeight={900}>Other Payments</Typography>
            {renderOtherTransactionsPage()}
          </Stack>
        )}

        {view === 'drawdown' && renderDrawdownView()}

        {paymentDialog}
        {poPaymentDialog}
        {newTransactionDialog}
        {rejectTransactionDialog}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Accounts"
        subtitle="Purchase order payments, certified task payments, and the project transactions register."
      />
      {!activeProjectId && <Alert severity="warning">Select an active project to load accounts data.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1.5 }}>Purchase Orders</Typography>
        {renderPurchaseOrderQueues('/procurement')}
      </Paper>

      {renderTaskQueueTable()}

      {renderOtherTransactionsPage()}

      {renderTransactionsTable()}

      {paymentDialog}
      {poPaymentDialog}
      {newTransactionDialog}
      {rejectTransactionDialog}
    </Stack>
  );
}
